import React, { useMemo, useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Icons from '../components/Icons';
import Avatar from '../components/ui/Avatar';
import AccessibilitySettingsDialog from '../components/AccessibilitySettingsDialog';
import BroadcastService from '../services/BroadcastService';
import BroadcastModal from '../components/broadcast/BroadcastModal';
import BroadcastBanner from '../components/broadcast/BroadcastBanner';
import RoleSettingsService from '../services/RoleSettingsService';
import { useUser } from '../store/UserContext';
import { useStudentStore } from '../store/StudentStore';
import { useSettings } from '../store/SettingsContext';
import { useDisplaySettings } from '../store/DisplaySettingsContext';
import { useFeaturePacks } from '../store/FeaturePackContext';
import { MODULE_PACK_MAP } from '../config/featurePacks';
import { formatLongDate } from '../utils/dateFormat';

export default function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, signOut } = useUser();
  const { students } = useStudentStore();
  const { settings } = useSettings();
  const { settings: displaySettings, setSettings: setDisplaySettings } = useDisplaySettings();
  const { packs } = useFeaturePacks();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const broadcastService = useMemo(() => new BroadcastService(), []);
  const roleService = useMemo(() => new RoleSettingsService(), []);
  const [activeBroadcast, setActiveBroadcast] = useState(null);
  const [, setRoleRevision] = useState(0);

  const criticalCount = useMemo(
    () => students.filter((s) => s.riskLevel === 'critical').length,
    [students]
  );

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Icons.Dashboard, path: '/dashboard' },
    { id: 'students', label: 'Students', icon: Icons.Students, path: '/students' },
    { id: 'support-workers', label: 'Support Workers', icon: Icons.Users, path: '/support-workers' },
    { id: 'safeguarding', label: 'Safeguarding', icon: Icons.Shield, path: '/students?view=safeguarding' },
    { id: 'incidents', label: 'Incidents', icon: Icons.Alert, path: '/incidents' },
    { id: 'broadcasts', label: 'Announcements', icon: Icons.Bell, path: '/broadcasts' },
    { id: 'messages', label: 'Internal Messages', icon: Icons.Messages, path: '/messages' },
    { id: 'external-messages', label: 'External Messages', icon: Icons.Messages, path: '/messages/external' },
    { id: 'programmes', label: 'Activities & Programmes', icon: Icons.Programmes, path: '/programmes' },
    { id: 'inventory', label: 'Inventory', icon: Icons.Inventory, path: '/inventory' },
    { id: 'attendance', label: 'Attendance', icon: Icons.Clock, path: '/attendance' },
    { id: 'compliance', label: 'Compliance', icon: Icons.Document, path: '/students?view=compliance' },
    { id: 'reports', label: 'Reports', icon: Icons.Reports, path: '/reports' },
    { id: 'settings', label: 'Settings', icon: Icons.Settings, path: '/settings' }
  ];

  const rolePermissions = user
    ? roleService.getPermissions(user.roleKey || (user.accessScope === 'admin' ? 'admin' : 'teacher'))
    : {};
  const canInternalMessages = Boolean(rolePermissions.messages);
  const canExternalMessages = Boolean(rolePermissions['external-messages']);

  const visibleNavItems = navItems.filter((item) => {
    if (!rolePermissions[item.id]) return false;
    const packId = MODULE_PACK_MAP[item.id];
    if (packId && packs?.[packId] === false) return false;
    return true;
  });

  const flatNavItems = visibleNavItems.flatMap((item) =>
    item.children ? [item, ...item.children] : [item]
  );

  const currentPath = `${location.pathname}${location.search}`;
  const currentLabel =
    [...flatNavItems]
      .sort((a, b) => b.path.length - a.path.length)
      .find((item) => currentPath.startsWith(item.path))?.label ||
    (location.pathname.startsWith('/safeguarding') ? 'Safeguarding' : 'Dashboard');

  const todayLabel = formatLongDate(new Date());
  const siteName = settings?.siteName || 'SafeguardPro';
  const siteSubtitle = settings?.siteSubtitle || 'Youth Centre Management';

  useEffect(() => {
    if (!user) return undefined;
    const refresh = () => {
      setActiveBroadcast(broadcastService.getActiveForUser(user.id));
    };
    refresh();
    const interval = window.setInterval(refresh, 60000);
    window.addEventListener('storage', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('storage', refresh);
    };
  }, [broadcastService, user]);

  useEffect(() => {
    const handleRolesUpdated = () => setRoleRevision((prev) => prev + 1);
    window.addEventListener('roles:updated', handleRolesUpdated);
    return () => window.removeEventListener('roles:updated', handleRolesUpdated);
  }, []);

  const handleAcknowledgeBroadcast = (broadcast) => {
    if (!broadcast || !user) return;
    broadcastService.acknowledge(broadcast, user.id);
    setActiveBroadcast(broadcastService.getActiveForUser(user.id));
  };

  return (
    <div className="sp-shell">
      <a href="#main-content" className="sp-skip-link">Skip to content</a>
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } sp-sidebar flex flex-col transition-all duration-300 fixed h-full z-40`}
        style={{ borderRightColor: 'var(--sp-border)' }}
      >
        <div className="p-4 border-b" style={{ borderBottomColor: 'var(--sp-border)' }}>
          <div className="flex items-center gap-3">
            {settings?.siteLogo ? (
              <div className="sp-panel sp-panel--muted w-10 h-10 rounded-full flex items-center justify-center overflow-hidden">
                <img src={settings.siteLogo} alt={`${siteName} logo`} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="sp-panel sp-panel--muted w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold">
                {(siteName || 'SP').slice(0, 2).toUpperCase()}
              </div>
            )}
            {sidebarOpen && (
              <div>
                <h1 className="sp-value" style={{ fontWeight: 600 }}>{siteName}</h1>
                <p className="text-xs sp-muted">{siteSubtitle}</p>
              </div>
            )}
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {visibleNavItems.map((item) => (
            <div key={item.id} className="sp-nav-group">
              <NavLink
                to={item.path}
                end={item.path === '/dashboard'}
                className={({ isActive }) => {
                  const active = item.path.includes('?') ? currentPath.startsWith(item.path) : isActive;
                  const safeguardingActive =
                    item.id === 'safeguarding' && location.pathname.startsWith('/safeguarding');
                  return `sp-nav-link ${active || safeguardingActive ? 'sp-nav-link--active' : ''}`;
                }}
              >
                {item.icon ? <item.icon /> : <span className="sp-nav-dot" />}
                {sidebarOpen && <span>{item.label}</span>}
                {sidebarOpen && item.id === 'safeguarding' && criticalCount > 0 && (
                  <span className="ml-auto sp-badge sp-badge--risk-critical">
                    {criticalCount}
                  </span>
                )}
              </NavLink>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t" style={{ borderTopColor: 'var(--sp-border)' }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="sp-button sp-button--secondary w-full"
          >
            {sidebarOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </aside>

      <main id="main-content" tabIndex={-1} className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <header className="sp-header px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="sp-value" style={{ fontSize: '18px', fontWeight: 600 }}>
                {currentLabel || 'Safeguarding Dashboard'}
              </h2>
              <p className="text-sm sp-muted">{todayLabel}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="sp-segment" role="radiogroup" aria-label="Theme mode">
                {['auto', 'light', 'dark'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={displaySettings.theme === mode}
                    className={displaySettings.theme === mode ? 'active' : ''}
                    onClick={() => setDisplaySettings((prev) => ({ ...prev, theme: mode }))}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
              <button
                className="sp-button sp-button--ghost"
                type="button"
                aria-label="Open accessibility and display settings"
                onClick={() => setAccessibilityOpen(true)}
              >
                <Icons.Settings />
              </button>
              <button className="sp-button sp-button--ghost relative" type="button">
                <Icons.Bell />
                {criticalCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: 'var(--sp-red)' }} />
                )}
              </button>
              <div className="relative">
                <button
                  type="button"
                  className="flex items-center gap-3"
                  onClick={() => setMenuOpen((prev) => !prev)}
                >
                  <Avatar name={user.name} size="sm" />
                  <div className="hidden md:block text-left">
                    <p className="text-sm" style={{ fontWeight: 600 }}>{user.name}</p>
                    <p className="text-xs sp-muted">{user.title}</p>
                  </div>
                </button>
                {menuOpen && (
                  <div className="sp-panel sp-panel--muted absolute right-0 mt-2 w-48 p-2 z-40">
                    <button
                      type="button"
                      className="sp-button sp-button--ghost w-full text-left"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/profile');
                      }}
                    >
                      My profile
                    </button>
                    {canInternalMessages && (
                      <button
                        type="button"
                        className="sp-button sp-button--ghost w-full text-left"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/messages');
                        }}
                      >
                        Internal messages
                      </button>
                    )}
                    {canExternalMessages && (
                      <button
                        type="button"
                        className="sp-button sp-button--ghost w-full text-left"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/messages/external');
                        }}
                      >
                        External messages
                      </button>
                    )}
                    <button
                      type="button"
                      className="sp-button sp-button--ghost w-full text-left"
                      onClick={() => {
                        setMenuOpen(false);
                        signOut();
                        navigate('/login');
                      }}
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          {activeBroadcast && (
            <div className="mb-4">
              <BroadcastBanner
                broadcast={activeBroadcast}
                onReview={() => setActiveBroadcast(broadcastService.getActiveForUser(user.id))}
              />
            </div>
          )}
          {user?.passwordMustChange && (
            <div className="sp-alert sp-alert--warning text-sm mb-4">
              This is a seeded account. Please update your password in your profile.
            </div>
          )}
          <Outlet />
        </div>
      </main>
      <AccessibilitySettingsDialog isOpen={accessibilityOpen} onClose={() => setAccessibilityOpen(false)} />
      {activeBroadcast?.ack_required && (
        <BroadcastModal broadcast={activeBroadcast} onAcknowledge={handleAcknowledgeBroadcast} />
      )}
    </div>
  );
}
